import type {
	TestShackReportInspection,
	TestShackReportItem,
} from "@/pages/products/test-shack/models/TestShackReport";
import type {
	TestShackReportCell,
	TestShackReportColumn,
	TestShackReportColumnGroup,
	TestShackReportSectionRev,
	TestShackReportSectionRevRow,
} from "../models/TestShackReportSectionRev";
import { hasInspectionTolerances } from "@/pages/products/test-shack/utils/hasInspectionTolerances";
import { YES_NO_CODES } from "../../constants/inspectionChecks";

const dimensionColumnKey = (dimensionId: number) => `dimension:${dimensionId}`;

const toDimensionColumn = (
	dimension: TestShackReportItem,
): TestShackReportColumn => ({
	key: dimensionColumnKey(dimension.ProfileItemId),
	code: dimension.Code,
	name: dimension.Name,
	displayName: dimension.DisplayName ?? dimension.Code,
	ltl: dimension.Ltl,
	nominal: dimension.Nominal,
	utl: dimension.Utl,
	// Yes/No checks have no Min/Max; gauges do, even though they are read as Go/No Go.
	hasSpec:
		YES_NO_CODES.has(dimension.Code) ||
		(dimension.Ltl !== null && dimension.Utl !== null),
	valueTypeCode: dimension.ValueTypeCode,
	isDegrees: dimension.IsDegrees,
});

const toRow = (
	inspection: TestShackReportInspection,
	columns: TestShackReportColumn[],
): TestShackReportSectionRevRow => {
	const cellsByColumn = new Map<string, TestShackReportCell>();
	for (const dimension of inspection.items) {
		cellsByColumn.set(dimensionColumnKey(dimension.ProfileItemId), {
			value: dimension.NumericValue,
			checked: dimension.Checked,
			status: dimension.Status,
		});
	}

	return {
		inspection,
		cells: columns.map((column) => cellsByColumn.get(column.key) ?? null),
		outOfSpecCount: inspection.OutOfSpecCount,
		hasTolerances: hasInspectionTolerances(inspection),
	};
};

const toSectionRev = (
	inspections: TestShackReportInspection[],
): TestShackReportSectionRev => {
	const [head] = inspections;
	const dimensions = new Map<number, TestShackReportItem>();
	for (const inspection of inspections) {
		for (const dimension of inspection.items) {
			const known = dimensions.get(dimension.ProfileItemId);
			if (
				known === undefined ||
				(known.Ltl === null &&
					known.Utl === null &&
					(dimension.Ltl !== null || dimension.Utl !== null))
			)
				dimensions.set(dimension.ProfileItemId, dimension);
		}
	}

	const sortedDimensions = [...dimensions.values()].toSorted(
		(left, right) =>
			left.CategorySortOrder - right.CategorySortOrder ||
			left.SortOrder - right.SortOrder ||
			left.Code.localeCompare(right.Code),
	);
	const columns = sortedDimensions.map(toDimensionColumn);
	const columnGroups: TestShackReportColumnGroup[] = [];
	for (const dimension of sortedDimensions) {
		const key = String(dimension.ProfileCategoryId);
		const previousGroup = columnGroups.at(-1);
		if (previousGroup?.key === key) previousGroup.columnCount += 1;
		else
			columnGroups.push({
				key,
				name:
					dimension.CategoryName === "Go / No Go"
						? "Gauges"
						: dimension.CategoryName,
				columnCount: 1,
			});
	}
	const rows = inspections.map((inspection) => toRow(inspection, columns));
	const productionTimes = inspections.map((inspection) =>
		inspection.ProductionDate.getTime(),
	);

	return {
		key: String(head.ProfileSpecId),
		sectionId: head.SectionId,
		sectionName: head.SectionName,
		specRev: head.SpecRev,
		specSubRev: head.SpecSubRev,
		millDrawingUploadFileId:
			inspections.find(
				(inspection) => inspection.MillDrawingUploadFileId !== null,
			)?.MillDrawingUploadFileId ?? null,
		columns,
		columnGroups,
		rows,
		outOfSpecCount: rows.reduce((total, row) => total + row.outOfSpecCount, 0),
		hasTolerances: rows.some((row) => row.hasTolerances),
		firstProductionDate: new Date(Math.min(...productionTimes)),
		lastProductionDate: new Date(Math.max(...productionTimes)),
	};
};

export const groupTestShackReportBySectionRev = (
	inspections: TestShackReportInspection[],
): TestShackReportSectionRev[] => {
	const inspectionsBySectionRev = new Map<
		number,
		TestShackReportInspection[]
	>();
	for (const inspection of inspections) {
		const known = inspectionsBySectionRev.get(inspection.ProfileSpecId);
		if (known) known.push(inspection);
		else inspectionsBySectionRev.set(inspection.ProfileSpecId, [inspection]);
	}

	return [...inspectionsBySectionRev.values()].map(toSectionRev);
};
